import { Component, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Link } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import Nav from "./components/Nav";
import Footer from "./components/Footer";
import ContactSection from "./components/ContactSection";
import BackToTop from "./components/BackToTop";
import Home from "./pages/Home";
import Work from "./pages/Work";
import CaseStudies from "./pages/CaseStudies";
import CaseStudyDetail from "./pages/CaseStudyDetail";
import Privacy from "./pages/Privacy";
import Owner from "./owner/Owner";
import { OwnerProvider } from "./owner/OwnerContext";
import { ContentProvider } from "./content/ContentContext";

export function scrollToHash(hash) {
  const el = document.getElementById(hash.replace("#", ""));
  if (!el) return false;
  const header = document.querySelector('[data-testid="site-header"]');
  const headerH = header ? header.offsetHeight : 64;
  const pad = parseFloat(getComputedStyle(el).paddingTop) || 0;
  // Land so the section's label/heading sits just below the sticky header (skip most of the padding).
  const top = el.getBoundingClientRect().top + window.scrollY + Math.max(0, pad - 28) - headerH;
  if (window.__lenis) window.__lenis.scrollTo(Math.max(0, top), { duration: 1.1 });
  else window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  return true;
}

function ScrollManager() {
  const { pathname, hash, key } = useLocation();
  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return; }
    let tries = 0;
    const attempt = () => { if (!scrollToHash(hash) && tries++ < 25) setTimeout(attempt, 120); };
    attempt();
  }, [pathname, hash, key]);
  return null;
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: false };
  }
  static getDerivedStateFromError() {
    return { error: true };
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: "80px 24px", textAlign: "center" }}>
          <h1 style={{ fontFamily: "Georgia, serif" }}>Something went wrong.</h1>
          <p style={{ color: "#6D6B78" }}>
            <Link to="/" style={{ color: "#563B78" }}>Back to the home page</Link>
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

function LenisSetup() {
  useEffect(() => {
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      window.__lenis = null;
      lenis.destroy();
    };
  }, []);
  return null;
}

function ContactRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/${search}#contact`} replace />;
}

function NotFound() {
  return (
    <main className="mx-auto max-w-3xl px-4 py-24 text-center sm:px-6 lg:px-8">
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-accent">404</p>
      <h1 className="mt-4 font-serif text-3xl font-semibold text-ink sm:text-4xl">
        This page doesn&rsquo;t exist.
      </h1>
      <Link
        to="/"
        data-testid="notfound-home-link"
        className="mt-8 inline-flex rounded-full bg-plum px-6 py-3 text-sm font-semibold text-white hover:bg-ink"
      >
        Back to home
      </Link>
    </main>
  );
}

function Shell() {
  return (
    <>
      <ScrollManager />
      <Nav />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<Navigate to="/#about" replace />} />
        <Route path="/services" element={<Navigate to="/#services" replace />} />
        <Route path="/pricing" element={<Navigate to="/#pricing" replace />} />
        <Route path="/work" element={<Work />} />
        <Route path="/case-studies" element={<CaseStudies />} />
        <Route path="/case-studies/:id" element={<CaseStudyDetail />} />
        <Route path="/contact" element={<ContactRedirect />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/owner" element={<Owner />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <ContactSection />
      <Footer />
      <BackToTop />
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <LenisSetup />
        <ContentProvider>
          <OwnerProvider>
            <Shell />
          </OwnerProvider>
        </ContentProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" />
    </ErrorBoundary>
  );
}
